<template>
  <div :class="wrapperClass">
    <div class="flex flex-row items-center justify-between">
      <div class="flex flex-row items-center">
        <UiLabel v-if="label">{{ label }}</UiLabel>
        <UiText v-if="mandatory" class="ml-1 text-negative-700">
          {{ $t('general.symbols.asterisk') }}
        </UiText>
      </div>
      <UiLink v-if="linkText && to" :id="`${id}-input`" :to="to" class="!typography-text-xs">
        {{ linkText }}
      </UiLink>
    </div>
    <SfInput
      v-bind="$attrs"
      :id="`${id}-input-${$attrs.type ?? 'text'}`"
      ref="inputRef"
      :data-testid="`${id}-input-${$attrs.type ?? 'text'}`"
      class="text-neutral-900 disabled:text-disabled-900"
      :class="[{ '!cursor-default !text-neutral-900': readOnly }]"
      :placeholder="placeholder ?? label"
      :size="size"
      :invalid="invalid"
      :disabled="disabled || readOnly"
      :wrapper-class="[
        'border-neutral-300 hover:border-primary-800 focus:border-primary-800',
        {
          '!border-disabled-300 !bg-disabled-100 hover:!ring-disabled-300 focus:!ring-disabled-300':
            disabled || readOnly,
        },
        inputWrapperClass,
        {
          '!rounded-none border border-transparent !ring-0': context === 'wrapped',
          '!border-negative-600': context === 'wrapped' && invalid,
        },
      ]"
    >
      <slot />
      <template #suffix>
        <slot name="suffix" />
      </template>
    </SfInput>
    <UiErrorMessage v-if="invalid && errorMessage" :message="errorMessage" />
    <UiHelpText v-if="helpText" :text="helpText" />
  </div>
</template>

<script setup lang="ts">
import type { VNodeRef } from 'vue';

import { unrefElement } from '@vueuse/core';

import type { InputExpose, InputProps } from '~/components/ui/Input/types';

const inputRef = ref<VNodeRef | null>(null);

const handleFocus = (): void => {
  const inputEl = unrefElement(inputRef)?.querySelector('input');
  inputEl?.focus();
};

defineExpose<InputExpose>({ handleFocus });

defineOptions({
  inheritAttrs: false,
});

withDefaults(defineProps<InputProps>(), {
  context: 'normal',
  size: 'base',
});
</script>
